/**
 * T-087 — the disclosure seam (kit half). An element with an `elementId` is shown when the user's
 * experience tier reaches the element's minimum tier in the disclosure registry; an element with
 * no registry row needs tier 4 (Expert). Hidden means not rendered for this user — the server
 * still refuses by role. The kit fetches nothing: the shell (web/src/app/experience.tsx) reads
 * the preference, the registry, the instance default and the view-as state and mounts the
 * provider. With no provider mounted every element shows (tier 4, the pre-R-14 behaviour).
 */
import * as React from 'react';

export type Tier = 1 | 2 | 3 | 4;
export interface DisclosureValue {
  tier: Tier;
  minTier: (elementId: string) => Tier;
}

const EXPERT: DisclosureValue = { tier: 4, minTier: () => 4 };
const Ctx = React.createContext<DisclosureValue>(EXPERT);

export function DisclosureProvider({ value, children }: { value: DisclosureValue; children: React.ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Visible at the user's tier: the pure rule, for lists that filter before rendering. */
export function disclosed(value: DisclosureValue, elementId: string | undefined): boolean {
  return !elementId || value.tier >= value.minTier(elementId);
}

export function useDisclosure(elementId: string): boolean {
  return disclosed(React.useContext(Ctx), elementId);
}

/** The whole value, for kit components that filter several elements (DataTable columns, Tabs). */
export function useDisclosureValue(): DisclosureValue {
  return React.useContext(Ctx);
}

/** A section shown only at its tier; `elementId` is its registry id. */
export function Disclosed({ elementId, children }: { elementId: string; children: React.ReactNode }) {
  return useDisclosure(elementId) ? <>{children}</> : null;
}
